import { useForm, Controller } from "react-hook-form";
import { useVerifyPinMutation } from "../../hooks/queries/useAuthQueries";
import { toastService } from "../../providers";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import {
  parsePinCode,
  computePinLockedExpiry,
  PIN_LOCK_FALLBACK_MS,
} from "../../services/utils/pinAuthUtils";
import { AUTH_STEP_UP_CODES } from "../../components/auth/config/authActionCodes";
import { pinValidator } from "../../lib/validations/authValidations";
import { usePinLoginBlockStore } from "../../stores/useOtpBlockStore";
import { usePinChallenge } from "./hooks/usePinChallenge";
import AuthLayout from "../../components/auth/authLayout/AuthLayout";
import AuthCard from "../../components/auth/authCard/AuthCard";
import PinChallengeCountdown from "../../components/auth/PinChallengeCountdown/PinChallengeCountdown";
import { CustomPinInput } from "../../components/ui/CustomPin/CustomPin";
import { CustomText } from "../../components/ui/CustomText/CustomText";
import CustomButton from "../../components/ui/CustomButton/CustomButton";

const getAttemptsRemaining = (data) =>
  data?.action?.attemptsRemaining ?? data?.attemptsRemaining;

const formatVerifyPinErrorMessage = (data) => {
  const attemptsRemaining = getAttemptsRemaining(data);
  if (attemptsRemaining == null) return "Incorrect PIN. Please try again.";
  if (attemptsRemaining <= 1) {
    return "Incorrect PIN. You have 1 attempt remaining. Your account will be temporarily locked if this attempt fails.";
  }
  return `Incorrect PIN. You have ${attemptsRemaining} attempts remaining before your account is temporarily locked.`;
};

function VerifyPinPage() {
  const { mutate: verifyPin, isPending } = useVerifyPinMutation();
  const {
    pinChallengeId,
    secondsLeft,
    redirectToLogin,
    completeLogin,
    navigate,
  } = usePinChallenge();

  const { control, handleSubmit, setError, reset, watch } = useForm({
    defaultValues: { pin: "" },
    mode: "onTouched",
  });

  const pin = watch("pin");
  const hasFormErrors = Boolean(pinValidator(pin));

  const onSubmit = (values) => {
    verifyPin(
      { challengeId: pinChallengeId, pin: values.pin },
      {
        onSuccess: (data) => {
          if (data.accessToken && data.refreshToken) {
            completeLogin(data, "Login successful");
            return;
          }

          const code = parsePinCode(null, data);

          if (code === AUTH_STEP_UP_CODES.PIN_INVALID) {
            reset({ pin: "" });
            setError("pin", { message: formatVerifyPinErrorMessage(data) });
            return;
          }

          if (code === AUTH_STEP_UP_CODES.PIN_LOCKED) {
            const blockExpiresAt = computePinLockedExpiry(
              data,
              PIN_LOCK_FALLBACK_MS,
            );
            usePinLoginBlockStore.getState().setOtpBlock(blockExpiresAt);
            redirectToLogin();
            return;
          }

          if (code === AUTH_STEP_UP_CODES.PIN_CHALLENGE_INVALID) {
            toastService.error(
              "Your session has expired. Please log in again.",
            );
            redirectToLogin("pin_challenge_expired");
            return;
          }

          if (code === AUTH_STEP_UP_CODES.PIN_SETUP_REQUIRED) {
            navigate("/auth/setup-pin");
            return;
          }

          toastService.error(
            data.remarks || "PIN verification failed. Please try again.",
          );
        },
        onError: (error) => {
          toastService.error(
            extractAxiosErrorMessage(
              error,
              "PIN verification failed. Please try again.",
            ),
          );
        },
      },
    );
  };

  return (
    <AuthLayout>
      <AuthCard
        subtitle={
          <div className="text-black-50 text-xs sm:text-[15px]">
            Enter your 6-digit PIN to verify your identity
          </div>
        }
      >
        <form
          className="flex flex-col gap-2.5 sm:gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            name="pin"
            control={control}
            rules={{ validate: (v) => pinValidator(v) ?? true }}
            render={({ field, fieldState }) => (
              <>
                <CustomPinInput
                  length={6}
                  mask
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={isPending}
                />
                {fieldState.error && (
                  <CustomText c="error" size="sm" align="center">
                    {fieldState.error.message}
                  </CustomText>
                )}
              </>
            )}
          />
          <PinChallengeCountdown secondsLeft={secondsLeft} />
          <div className="flex justify-end text-xs sm:text-sm">
            <a
              href="/auth/forgot-pin"
              className="text-primary-500 hover:underline"
            >
              Forgot PIN?
            </a>
          </div>
          <CustomButton
            type="submit"
            radius="xl"
            size="md"
            fullWidth
            disabled={isPending || hasFormErrors}
            loading={isPending}
          >
            Verify
          </CustomButton>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}

export default VerifyPinPage;
